import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Save, ArrowLeft, User, Copy } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';

// Define the form schema
const formSchema = z.object({
  creator_name: z.string().min(1, 'Creator name is required'),
  joyabuy: z.string().optional(),
  kakobuy: z.string().optional(),
  cnfans: z.string().optional(),
  hoobuy: z.string().optional(),
  acbuy: z.string().optional(),
  oopbuy: z.string().optional(),
  mulebuy: z.string().optional(),
  orientdig: z.string().optional(),
  superbuy: z.string().optional(),
  ootdbuy: z.string().optional(),
  cssbuy: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const CreatorForm = () => {
  const navigate = useNavigate();
  const { id: creatorName } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(creatorName ? true : false);
  const isEditMode = Boolean(creatorName);

  // Initialize form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      creator_name: '',
      joyabuy: '',
      kakobuy: '',
      cnfans: '',
      hoobuy: '',
      acbuy: '',
      oopbuy: '',
      mulebuy: '',
      orientdig: '',
      superbuy: '',
      ootdbuy: '',
      cssbuy: '',
    },
  });

  // Load creator data if in edit mode
  useEffect(() => {
    if (isEditMode && creatorName) {
      const fetchCreator = async () => {
        try {
          setInitialLoading(true);
          const decodedCreatorName = decodeURIComponent(creatorName);
          const { data, error } = await supabase
            .from('creators')
            .select('*')
            .eq('creator_name', decodedCreatorName)
            .single();

          if (error) {
            throw error;
          }

          if (data) {
            form.reset({
              creator_name: data.creator_name || '',
              joyabuy: data.joyabuy || '',
              kakobuy: data.kakobuy || '',
              cnfans: data.cnfans || '',
              hoobuy: data.hoobuy || '',
              acbuy: data.acbuy || '',
              oopbuy: data.oopbuy || '',
              mulebuy: data.mulebuy || '',
              orientdig: data.orientdig || '',
              superbuy: data.superbuy || '',
              ootdbuy: data.ootdbuy || '',
              cssbuy: data.cssbuy || '',
            });
          }
        } catch (error) {
          console.error('Error fetching creator:', error);
          toast.error('Failed to load creator data');
          navigate('/admin/creators');
        } finally {
          setInitialLoading(false);
        }
      };

      fetchCreator();
    }
  }, [creatorName, isEditMode, navigate, form]);

  const onSubmit = async (data: FormValues) => {
    try {
      setLoading(true);

      if (isEditMode && creatorName) {
        // Update existing creator
        const decodedCreatorName = decodeURIComponent(creatorName);
        const { error } = await supabase
          .from('creators')
          .update(data)
          .eq('creator_name', decodedCreatorName);

        if (error) throw error;
        toast.success('Creator updated successfully');
      } else {
        // Create new creator
        const { error } = await supabase
          .from('creators')
          .insert([{
            creator_name: data.creator_name,
            joyabuy: data.joyabuy || '',
            kakobuy: data.kakobuy || '',
            cnfans: data.cnfans || '',
            hoobuy: data.hoobuy || '',
            acbuy: data.acbuy || '',
            oopbuy: data.oopbuy || '',
            mulebuy: data.mulebuy || '',
            orientdig: data.orientdig || '',
            superbuy: data.superbuy || '',
            ootdbuy: data.ootdbuy || '',
            cssbuy: data.cssbuy || ''
          }]);

        if (error) throw error;
        toast.success('Creator added successfully');
      }

      navigate('/admin/creators');
    } catch (error) {
      console.error('Error saving creator:', error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'add'} creator`);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCreatorLink = async (creatorName: string) => {
    try {
      const creatorLink = `https://replibrary.com?creator=${encodeURIComponent(creatorName)}`;
      await navigator.clipboard.writeText(creatorLink);
      toast.success('Creator link copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy link to clipboard');
    }
  };

  return (
    <div className="space-y-6 w-full">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
        <div>
          <h1 className="text-3xl font-bold mb-1">{isEditMode ? 'Edit Creator' : 'Add Creator'}</h1>
          <p className="text-muted-foreground">
            {isEditMode ? 'Update creator information and buying aff codes' : 'Add a new creator with buying aff codes'}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
          <Link to="/admin/creators">
            <Button variant="outline" className="flex items-center gap-2">
              <ArrowLeft size={16} />
              Back to Creators
            </Button>
          </Link>
        </div>
      </div>

      {initialLoading ? (
        <div className="grid place-items-center h-60 bg-card rounded-lg border">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
            <p className="text-sm text-muted-foreground">Loading creator data...</p>
          </div>
        </div>
      ) : (
        <Card>
          <CardHeader className="border-b bg-card/50">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              <div>
                <CardTitle>{isEditMode ? 'Edit Creator Details' : 'Creator Details'}</CardTitle>
                <CardDescription>Enter creator information and buying aff codes</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-6 md:col-span-2">
                    <FormField
                      control={form.control}
                      name="creator_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Creator Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter creator name" {...field} />
                          </FormControl>
                          <FormDescription>
                            The unique name of the creator
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    {/* Creator URL Display */}
                    {form.watch('creator_name') && (
                      <div className="bg-muted/30 rounded-lg p-4 border">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1">
                            <label className="text-sm font-medium text-muted-foreground mb-1 block">
                              Creator URL
                            </label>
                            <div className="text-sm font-mono bg-background px-3 py-2 rounded border break-all">
                              https://replibrary.com?creator={encodeURIComponent(form.watch('creator_name'))}
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopyCreatorLink(form.watch('creator_name'))}
                            className="flex items-center gap-2 shrink-0"
                          >
                            <Copy className="h-4 w-4" />
                            Copy
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-1">
                    <h3 className="text-sm font-medium text-muted-foreground mb-3 pt-2 border-t">Buying aff codes</h3>
                    <FormField
                      control={form.control}
                      name="joyabuy"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Joyabuy</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter joyabuy aff code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-1 pt-9">
                    <FormField
                      control={form.control}
                      name="kakobuy"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Kakobuy</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter kakobuy aff code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-1">
                    <FormField
                      control={form.control}
                      name="cnfans"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>CNFans</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter cnfans aff code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-1">
                    <FormField
                      control={form.control}
                      name="hoobuy"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Hoobuy</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter hoobuy aff code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-1">
                    <FormField
                      control={form.control}
                      name="acbuy"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ACbuy</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter acbuy aff code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-1">
                    <FormField
                      control={form.control}
                      name="oopbuy"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Oopbuy</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter oopbuy aff code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-1">
                    <FormField
                      control={form.control}
                      name="mulebuy"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Mulebuy</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter mulebuy aff code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-1">
                    <FormField
                      control={form.control}
                      name="orientdig"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Orientdig</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter orientdig aff code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-1">
                    <FormField
                      control={form.control}
                      name="superbuy"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Superbuy</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter superbuy aff code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-1">
                    <FormField
                      control={form.control}
                      name="ootdbuy"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Ootdbuy</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter ootdbuy aff code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-1">
                    <FormField
                      control={form.control}
                      name="cssbuy"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>CSSbuy</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter cssbuy aff code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Link to="/admin/creators">
                    <Button variant="outline" type="button">Cancel</Button>
                  </Link>
                  <Button type="submit" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isEditMode ? 'Updating...' : 'Saving...'}
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        {isEditMode ? 'Update Creator' : 'Save Creator'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CreatorForm; 